import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  useMemo,
  KeyboardEvent,
} from 'react';
import {
  Frame,
  Header,
  Container,
  Content,
  Menu,
  MenuItem,
  Input,
} from '@kinesis/bungle';
import { noop, isEmpty } from 'lodash/fp';
import Fuse from 'fuse.js';
import { DisclosureDefinition, DisclosureType } from '@/types';
import { disclosureTypes } from '@/data/disclosures';

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: false,
  minMatchCharLength: 2,
  keys: ['name'],
};

// Searchable type picker for creating a disclosure (ported from suit's
// `NewDisclosure`). Lists every type in the `disclosureTypes` registry,
// optionally narrowed by `availableTypes`; pick one to create a draft of that
// type. The list — not a button-per-type — is what scales the create flow.
const NewDisclosure = forwardRef((props: NewDisclosureProps, ref) => {
  const { onCreate = noop, onClose = noop, availableTypes } = props;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const enabled = useMemo(
    () =>
      availableTypes
        ? disclosureTypes.filter((d) => availableTypes.includes(d.type))
        : disclosureTypes,
    [availableTypes],
  );
  const filtered = useMemo(() => {
    if (isEmpty(search)) return enabled;
    const fuse = new Fuse(enabled, fuseOptions);
    return fuse.search(search).map(({ item }) => item);
  }, [search, enabled]);
  const onCreateByType = useCallback(
    (type: DisclosureType) => {
      onCreate({ type });
    },
    [onCreate],
  );
  const onSearch = useCallback((v: string) => {
    setSelected(0);
    setSearch(v);
  }, []);
  const onHover = useCallback((index: number) => {
    setSelected(index);
  }, []);
  const onCancelSearch = useCallback(
    (event: KeyboardEvent) => {
      if (!isEmpty(search) && event.key === 'Escape') {
        event.stopPropagation();
        onSearch('');
      } else if (event.key === 'Escape') {
        onClose();
      }
    },
    [onSearch, search, onClose],
  );
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelected((v) => (v < filtered.length - 1 ? v + 1 : 0));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelected((v) => (v > 0 ? v - 1 : filtered.length - 1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const v = filtered[selected]?.type;
        if (v) {
          onCreateByType(v);
          onClose();
        }
      }
    },
    [filtered, selected, onCreateByType, onClose],
  );

  return (
    <div ref={ref as any} style={{ height: '100%' }} onKeyDown={onKeyDown}>
      <Content
        border
        shadow
        borderRadiusSize='large'
        background='white'
        maxWidth={400}
      >
        <Container>
          <Header>
            <Content padding='small' paddingMode='equal' background='white'>
              <Input
                ref={inputRef}
                type='search'
                onChange={onSearch}
                onKeyDown={onCancelSearch}
                value={search}
                maxWidth='100%'
                magnitude='large'
                appearance='bare'
                placeholder='Disclosure type'
                autoFocus
                autoComplete='off'
              />
            </Content>
          </Header>
          <Frame>
            <Content
              background='white'
              padding='small'
              paddingMode='equal'
              height={320}
            >
              <Menu>
                {filtered.map((d, index) => (
                  <MenuItem
                    key={d.type}
                    icon={d.icon}
                    variant={index === selected ? 'focused' : 'default'}
                    content={d.name}
                    onClick={() => {
                      onCreateByType(d.type);
                      onClose();
                    }}
                    onHover={() => onHover(index)}
                  />
                ))}
              </Menu>
            </Content>
          </Frame>
        </Container>
      </Content>
    </div>
  );
});

NewDisclosure.displayName = 'NewDisclosure';

export type NewDisclosureProps = {
  onCreate?: (v: DisclosureDefinition) => void;
  onClose?: () => void;
  availableTypes?: string[];
};

export { NewDisclosure };
